import React, {forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from "react";
import OwlCarousel from "react-owl-carousel";
import classnames from "classnames";
import Sizes from "../../../../../../../core/models/static/sizes";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import useWindowViewportWidth from "../../../../../../hooks/use-window/viewport-width";
import {UserHomepageProductContext} from "../../../../index";
import UserPhoneHomepageProductCard from "../product-card";

// Default values for the responsive viewport for the items inside the carousel based on the view size
const defaultResponsive = {
    [Sizes.xs]: {items: 1.2},
}

const UserPhoneHomepageProductSlider = forwardRef(({
                                                       products,
                                                       type,
                                                       responsive = defaultResponsive,
                                                       onSelectionChanged,
                                                       onProductClicked,
                                                       onTagClicked,
                                                       carouselSlideSpeed = 500,
                                                   }, ref) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const viewportWidth = useWindowViewportWidth();
    const [childHeight, setChildHeight] = useState(0);
    const initialRender = useRef(true);
    /**@type {React.MutableRefObject<import('react-owl-carousel/umd/OwlCarousel.d.ts').default>}*/
    const carouselRef = useRef();
    const containerIdRef = useRef(createUUId(true));
    const [forceUpdate, setForceUpdate] = useState(false);
    const prevProductsLength = useRef(products?.length ?? 0);
    const setSettingsRef = useRef(false);

    /**
     * Exposes the carousel api ref functionalities to the parent component.
     */
    useImperativeHandle(ref, () => carouselRef.current, [carouselRef])

    /**
     * Before each change in the products' length:
     * - sets the prevProductsLength ref value with the previous length of the products.
     */
    useLayoutEffect(() => {
        const length = products?.length ?? 0;
        setSettingsRef.current = false;
        return () => {
            prevProductsLength.current = length;
        }
    }, [products?.length])

    /**
     * With each change in the products:
     * - re-renders of the component (specifically the carousel component) to ensure the updated products are shown
     * in the ui
     */
    useEffect(() => {
        if (initialRender.current)
            return;
        setForceUpdate(prevState => !prevState);
    }, [products])

    /**
     * For Each change in view port width and containerIdRef:
     * - Sets a timer for getting all the elements in the carousel
     * - Calculates the max height of the elements in the list and sets the value in the context
     * - Increases the max height only in init render to account for box shadow rendering
     */
    useEffect(() => {
        if (!products?.length || !initialRender.current)
            return;
        initialRender.current = false;
        let initialHeightUpdate = true;
        const timer = setTimeout(() => {
            const containerElement = document.getElementById(containerIdRef.current);

            if (!containerElement)
                return;

            const products = containerElement.getElementsByClassName('product-container');
            if (!products?.length)
                return;

            let height = [...products, {dummy: true}, {dummy: true}]?.reduce((p, c) => {
                if (c?.dummy) {
                    return p
                }
                return Math.max(p, c.getBoundingClientRect().height)
            }, 0);

            if (initialHeightUpdate) {
                height += 10;
                initialHeightUpdate = false;
            }
            setChildHeight(height);

        }, 500)
        return () => clearTimeout(timer);
    }, [products, containerIdRef, viewportWidth])

    /**
     * With every change in view port size:
     * - navigates the carousel to the current index
     */
    useLayoutEffect(() => {
        carouselRef.current.to(currentIndex, carouselSlideSpeed);
    }, [viewportWidth, childHeight]);

    /**
     * Changes the values of [hasNext] and [hasPrev] depending on the current state of the owl
     * carousel.
     * @param {Event} e
     */
    const onOwlCarouselChanged = (e) => {
        if (e?.item?.index === null)
            return;
        if (e?.property?.name !== 'position') {

            if (setSettingsRef.current)
                return;
            setSettingsRef.current = true;

            if (prevProductsLength.current !== products.length) {
                setCurrentIndex(prevState => {
                    if (prevProductsLength.current > products.length) {
                        // product was removed
                        const newIndex = Math.max(Math.min(prevState, products.length - 1), 0);
                        carouselRef.current?.to(newIndex, 0);
                        return newIndex;
                    } else if (prevProductsLength.current < products.length) {
                        // product was added
                        const newIndex = Math.max(products.length - 1, 0);
                        carouselRef.current?.to(newIndex, 0);
                        return newIndex;
                    }
                    return prevState;
                })
            }
            return
        }

        const itemsInfo = e?.item ?? {};

        setCurrentIndex(itemsInfo.index);

        if (onSelectionChanged && currentIndex !== itemsInfo.index) {
            onSelectionChanged(products[itemsInfo.index], itemsInfo.index);
        }
    }

    const owlCarousel = useMemo(() =>
            <OwlCarousel
                center
                autoplay={false}
                loop={false}
                margin={0}
                responsive={responsive}
                ref={carouselRef}
                onChanged={onOwlCarouselChanged}
                id={containerIdRef.current}
                className={'owl-products'}>
                {
                    products?.map((item) => (
                        <div key={item?.id} className={classnames('p-3')}>
                            <UserPhoneHomepageProductCard
                                isChildOfCarousel
                                data={item}
                                type={type}
                                onProductClicked={onProductClicked}
                                onTagClicked={onTagClicked}
                            />
                        </div>
                    ))
                }
            </OwlCarousel>,
        [responsive, viewportWidth, childHeight, products, type, forceUpdate])

    return (
        <>
            <UserHomepageProductContext.Provider value={{enforcedHeight: childHeight}}>
                <div className={'user-phone-homepage-product-slider'}>
                    {owlCarousel}
                </div>
            </UserHomepageProductContext.Provider>
        </>
    )
})

export default UserPhoneHomepageProductSlider;
